import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { useCart } from '../contexts/CartContext';
import { toast } from '../hooks/use-toast';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Category page — pulls categories dynamically from the /categories API so
// new categories added in Admin appear immediately without a code change.

const Category = () => {
  const { categoryId } = useParams();
  const [products, setProducts] = useState([]);
  const [categoryName, setCategoryName] = useState('');
  const [categoryExists, setCategoryExists] = useState(true);
  const [loading, setLoading] = useState(true);
  const [displaySettings, setDisplaySettings] = useState({
    show_prices: true,
    show_stock: true
  });
  const { addToCart } = useCart();

  useEffect(() => {
    fetchData();
  }, [categoryId]);

  const fetchData = async () => {
    try {
      setLoading(true);
      const [productsRes, settingsRes, categoriesRes] = await Promise.all([
        axios.get(`${API}/products`, { params: { category: categoryId } }),
        axios.get(`${API}/settings`),
        axios.get(`${API}/categories`),
      ]);
      setProducts(productsRes.data);
      setDisplaySettings(settingsRes.data);
      const match = (categoriesRes.data || []).find((c) => c.slug === categoryId);
      if (match) {
        setCategoryName(match.name);
        setCategoryExists(true);
      } else {
        // Fallback: humanize the slug so unknown categories still render gracefully
        setCategoryName((categoryId || '').replace(/-/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase()));
        setCategoryExists(false);
      }
    } catch (error) {
      console.error('Error fetching data:', error);
      toast({
        title: "Error",
        description: "Failed to load products",
        variant: "destructive"
      });
    } finally {
      setLoading(false);
    }
  };

  const handleAddToCart = (product, e) => {
    e.preventDefault();
    addToCart(product);
    toast({
      title: "Added to cart",
      description: `${product.name} has been added to your cart.`,
    });
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-purple-600"></div>
      </div>
    );
  }

  if (!categoryExists && products.length === 0) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center px-4">
        <div className="text-center">
          <h2 className="text-xl sm:text-2xl font-bold text-slate-800 mb-4">Category not found</h2>
          <Link to="/products">
            <Button>Back to Products</Button>
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50">
      <div className="bg-gradient-to-r from-purple-600 to-slate-700 text-white py-8 sm:py-12">
        <div className="container mx-auto px-4">
          <h1 className="text-2xl sm:text-4xl font-bold mb-2">{categoryName}</h1>
          <p className="text-base sm:text-lg opacity-90">{products.length} products available</p>
        </div>
      </div>

      <div className="container mx-auto px-4 py-4 sm:py-8">
        {products.length === 0 ? (
          <div className="text-center py-12">
            <p className="text-lg sm:text-xl text-slate-600 mb-4">No products found in this category.</p>
            <Link to="/products">
              <Button>Browse All Products</Button>
            </Link>
          </div>
        ) : (
          <div className="grid grid-cols-2 sm:grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-6">
            {products.map(product => (
              <Link key={product.id} to={`/product/${product.id}`} className="group">
                <Card className="h-full hover:shadow-xl transition-all duration-300 border-2 hover:border-purple-500">
                  <CardContent className="p-2 sm:p-4">
                    <div className="aspect-square bg-slate-100 rounded-lg mb-2 sm:mb-4 overflow-hidden">
                      <img
                        src={product.image}
                        alt={product.name}
                        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                      />
                    </div>
                    <h3 className="font-semibold text-xs sm:text-base text-slate-800 mb-1 sm:mb-2 line-clamp-2 group-hover:text-purple-600 transition-colors">
                      {product.name}
                    </h3>
                    <p className="text-xs text-slate-500 mb-2 sm:mb-3 line-clamp-2 hidden sm:block">{product.description}</p>
                    <div className="flex flex-col gap-2 sm:gap-3">
                      <div className="flex flex-col sm:flex-row justify-between sm:items-center gap-1">
                        {displaySettings.show_prices ? (
                          <span className="text-base sm:text-xl font-bold text-purple-600">${(product.retail_price ?? product.price)}</span>
                        ) : (
                          <span className="text-xs sm:text-sm text-slate-500 italic">Contact for pricing</span>
                        )}
                        {displaySettings.show_stock ? (
                          <span className={`text-xs sm:text-sm ${product.stock > 0 ? 'text-green-600' : 'text-red-600'}`}>
                            {product.stock > 0 ? `${product.stock} in stock` : 'Out of Stock'}
                          </span>
                        ) : (
                          <span className={`text-xs sm:text-sm ${product.stock > 0 ? 'text-green-600' : 'text-red-600'}`}>
                            {product.stock > 0 ? 'In Stock' : 'Out of Stock'}
                          </span>
                        )}
                      </div>
                      <Button
                        className="w-full bg-purple-600 hover:bg-purple-700 text-white text-xs sm:text-sm py-1 sm:py-2"
                        onClick={(e) => handleAddToCart(product, e)}
                        disabled={product.stock === 0}
                      >
                        Add to Cart
                      </Button>
                    </div>
                  </CardContent>
                </Card>
              </Link>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default Category;
